import { CardDef, CardInstance, PlayerState, CardTier } from '../types';
import { CardPool } from './CardPool';
import { getCardById } from '../cards';

// ラウンドごとの選択肢
export interface TierOption {
  tier: CardTier;
  selectCount: number;
}

// ラウンドごとの設定
export const ROUND_CONFIG: Record<number, { availableTiers: TierOption[] }> = {
  1: { availableTiers: [{ tier: 'A', selectCount: 2 }] },
  2: { availableTiers: [{ tier: 'A', selectCount: 2 }, { tier: 'B', selectCount: 1 }] },
  3: { availableTiers: [{ tier: 'A', selectCount: 2 }, { tier: 'B', selectCount: 1 }] },
  4: { availableTiers: [{ tier: 'A', selectCount: 2 }, { tier: 'B', selectCount: 1 }, { tier: 'C', selectCount: 1 }] },
  5: { availableTiers: [{ tier: 'A', selectCount: 2 }, { tier: 'B', selectCount: 2 }, { tier: 'C', selectCount: 1 }] },
  6: { availableTiers: [{ tier: 'A', selectCount: 2 }, { tier: 'B', selectCount: 2 }, { tier: 'C', selectCount: 1 }] },
  7: { availableTiers: [{ tier: 'B', selectCount: 2 }, { tier: 'C', selectCount: 2 }] },
};

// 共通山札
interface SharedDeck {
  tierA: CardDef[];
  tierB: CardDef[];
  tierC: CardDef[];
  discardPile: CardDef[];
}

// プレイヤーの状態
interface PlayerDrawState {
  selectedTier: CardTier | null;
  drawnCards: CardDef[];
  maxSelections: number;
  hasRedrawn: boolean;
}

export class DeckPhase {
  private cardPool: CardPool;
  private currentRound: number;
  private sharedDeck: SharedDeck;
  private playerStates: Map<string, PlayerDrawState> = new Map();
  private availableTiers: TierOption[];

  constructor(cardPool: CardPool, round: number, customTierDraws?: Record<CardTier, number>[]) {
    this.cardPool = cardPool;
    this.currentRound = round;
    
    // カスタム設定があればそれを使用、なければデフォルト
    if (customTierDraws && customTierDraws[round - 1]) {
      const draws = customTierDraws[round - 1];
      this.availableTiers = this.tierDrawsToOptions(draws);
    } else {
      const config = ROUND_CONFIG[round] ?? ROUND_CONFIG[7];
      this.availableTiers = config.availableTiers;
    }
    
    this.sharedDeck = this.initializeSharedDeck();
  }

  // tierDraws形式をTierOption[]に変換
  private tierDrawsToOptions(draws: Record<CardTier, number>): TierOption[] {
    const result: TierOption[] = [];
    const tiers: CardTier[] = ['A', 'B', 'C'];
    for (const tier of tiers) {
      if (draws[tier] > 0) {
        result.push({ tier, selectCount: draws[tier] });
      }
    }
    return result;
  }

  private initializeSharedDeck(): SharedDeck {
    const allCards = this.cardPool.getUnlockedCards();
    return {
      tierA: this.shuffleArray(allCards.filter(c => c.tier === 'A')),
      tierB: this.shuffleArray(allCards.filter(c => c.tier === 'B')),
      tierC: this.shuffleArray(allCards.filter(c => c.tier === 'C')),
      discardPile: [],
    };
  }

  private shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  private getTierDeck(tier: CardTier): CardDef[] {
    switch (tier) {
      case 'A': return this.sharedDeck.tierA;
      case 'B': return this.sharedDeck.tierB;
      case 'C': return this.sharedDeck.tierC;
    }
  }

  getAvailableTiers(): TierOption[] {
    return this.availableTiers;
  }

  getPlayerState(playerId: string): PlayerDrawState | null {
    return this.playerStates.get(playerId) ?? null;
  }

  // Tier選択して5枚引く
  selectTier(playerId: string, tier: CardTier): CardDef[] | null {
    const existing = this.playerStates.get(playerId);
    if (existing && existing.selectedTier !== null) {
      return existing.drawnCards;
    }

    const tierOption = this.availableTiers.find(t => t.tier === tier);
    if (!tierOption) return null;

    const deck = this.getTierDeck(tier);
    const drawnCards = deck.splice(0, Math.min(5, deck.length));

    this.playerStates.set(playerId, {
      selectedTier: tier,
      drawnCards,
      maxSelections: tierOption.selectCount,
      hasRedrawn: false,
    });

    return drawnCards;
  }

  // 引き直し (1枚)
  redrawCard(playerId: string, cardId: string): CardDef | null {
    const state = this.playerStates.get(playerId);
    if (!state || state.selectedTier === null || state.hasRedrawn) return null;

    const cardIdx = state.drawnCards.findIndex(c => c.id === cardId);
    if (cardIdx === -1) return null;

    const oldCard = state.drawnCards[cardIdx];
    const deck = this.getTierDeck(state.selectedTier);
    deck.push(oldCard);
    this.shuffleArray(deck);

    const newCard = deck.shift();
    if (!newCard) {
      state.drawnCards[cardIdx] = oldCard;
      return null;
    }

    state.drawnCards[cardIdx] = newCard;
    state.hasRedrawn = true;
    return newCard;
  }

  canRedraw(playerId: string): boolean {
    const state = this.playerStates.get(playerId);
    return state !== null && state !== undefined && !state.hasRedrawn;
  }

  // 選択したカードをデッキに追加 (UIから選択カードIDを渡す)
  addSelectedCards(player: PlayerState, selectedCardIds: string[]): CardInstance[] {
    const state = this.playerStates.get(player.info.id);
    if (!state || state.selectedTier === null) return [];

    const instances: CardInstance[] = [];

    for (const card of state.drawnCards) {
      if (selectedCardIds.includes(card.id)) {
        const instance = this.cardPool.createInstance(card);
        player.deck.push(instance);
        instances.push(instance);
      } else {
        this.sharedDeck.discardPile.push(card);
      }
    }

    return instances;
  }

  hasTierSelected(playerId: string): boolean {
    const state = this.playerStates.get(playerId);
    return state !== undefined && state !== null && state.selectedTier !== null;
  }

  getDeckCounts(): Record<CardTier, number> {
    return {
      A: this.sharedDeck.tierA.length,
      B: this.sharedDeck.tierB.length,
      C: this.sharedDeck.tierC.length,
    };
  }

  // 1枚除外 (instanceIdで指定)
  removeCard(player: PlayerState, instanceId: string): boolean {
    const idx = player.deck.findIndex(c => c.instanceId === instanceId);
    if (idx === -1) return false;
    player.deck.splice(idx, 1);
    return true;
  }

  getDeckInfo(player: PlayerState): {
    cards: { instance: CardInstance; def: CardDef }[];
    categoryCount: Record<string, number>;
    tierCount: Record<CardTier, number>;
    totalPower: number;
    avgPower: number;
  } {
    const cards = player.deck.map(instance => ({
      instance,
      def: getCardById(instance.defId)!,
    })).filter(c => c.def);

    const categoryCount: Record<string, number> = {};
    const tierCount: Record<CardTier, number> = { A: 0, B: 0, C: 0 };
    let totalPower = 0;

    for (const { instance, def } of cards) {
      categoryCount[def.category] = (categoryCount[def.category] ?? 0) + 1;
      tierCount[def.tier]++;
      totalPower += instance.currentPower;
    }

    return {
      cards,
      categoryCount,
      tierCount,
      totalPower,
      avgPower: cards.length > 0 ? totalPower / cards.length : 0,
    };
  }

  getCurrentRound(): number {
    return this.currentRound;
  }

  resetForNextRound(): void {
    for (const card of this.sharedDeck.discardPile) {
      this.getTierDeck(card.tier).push(card);
    }
    this.sharedDeck.discardPile = [];
    this.sharedDeck.tierA = this.shuffleArray(this.sharedDeck.tierA);
    this.sharedDeck.tierB = this.shuffleArray(this.sharedDeck.tierB);
    this.sharedDeck.tierC = this.shuffleArray(this.sharedDeck.tierC);
    this.playerStates.clear();
  }
}
